import React, { useState } from 'react';
import { useTheme } from 'styled-components';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { Paragraph, TextField } from 'common/components';
import { openInNewTab } from 'common/helpers';
import { Button, checkPasswordStrength, PasswordField, PasswordStrength } from 'components/common';
import { Divider } from 'components/styled/Divider';
import { Flex } from 'components/styled/Flex';
import { LoginSignupWrapper } from 'components/styled/Form.styled';
import RegistrationHeader from 'components/styled/RegistrationHeader';
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from 'consts/contactInfo';
import { keysToLowercase } from 'helper/objects';
import { ButtonSpacing, LoginLink } from 'modules/account/features/login/components/index.styled';
import { useMutationSignUp } from 'modules/account/features/singup/hooks';
import { TPasswordStrength } from 'modules/account/features/singup/models';
import { useAuthState } from 'services/auth/AuthContext';
import { PlansAndPricing } from '../components/PlansAndPricing/PlansAndPricing';

export const SignupSchema = Yup.object().shape({
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  field_a: Yup.string().email('Invalid email').required('Required'),
  confirmEmail: Yup.string()
    .email('Invalid email')
    .required('Required')
    .oneOf([Yup.ref('field_a'), null], "Emails don't match")
    .required('Required'),
  field_b: Yup.string().required('Required'),
});

export const SignUp = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { colors, fontFamily } = useTheme();
  const {
    state: { isRegistrationPending },
    registerUser,
  } = useAuthState();

  const { executeRecaptcha } = useGoogleReCaptcha();

  const [passwordStrength, setPasswordStrength] = useState<TPasswordStrength[]>([]);

  //Mutation hooks
  const { error, mutate, status } = useMutationSignUp({ registerUser });

  if (isRegistrationPending) {
    return <Navigate to="/sign-up-success" />;
  }

  const createAccountSubmitForm = async (formValues: any) => {
    const isValidPassword = passwordStrength.every((item: TPasswordStrength) => item.isValid);

    if (!isValidPassword || !executeRecaptcha) return;

    // Generate reCAPTCHA token
    const token = await executeRecaptcha('signUp');

    const { field_a, field_b, ...restValues } = formValues;
    const payload = {
      ...restValues,
      email: formValues.field_a,
      password: formValues.field_b,
      recaptchaToken: token,
    };

    mutate(payload);
  };

  return (
    <>
      {location.state?.packageID ? (
        <>
          <RegistrationHeader mb={false}>{t('Create an account')}</RegistrationHeader>
          <Formik
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            initialErrors={error ? keysToLowercase(error.response.data.Items) : null}
            enableReinitialize
            validationSchema={SignupSchema}
            initialValues={{
              firstName: '',
              lastName: '',
              field_a: '',
              packageId: location.state?.packageID,
              confirmEmail: '',
              field_b: '',
            }}
            onSubmit={createAccountSubmitForm}
            validate={(values: any) => {
              const { field_b } = values;
              const passwordValidation = checkPasswordStrength(field_b);
              setPasswordStrength(passwordValidation);
            }}
          >
            {({ errors, touched }) => (
              <LoginSignupWrapper bgColor={colors.black_5}>
                <TextField
                  label={t('First name')}
                  name="firstName"
                  isRequired
                  labelStyle={{ backgroundColor: colors.black_5 }}
                />

                <TextField
                  label={t('Last name')}
                  name="lastName"
                  isRequired
                  labelStyle={{ backgroundColor: colors.black_5 }}
                />

                <TextField
                  label={t('Email')}
                  name="field_a"
                  disableAutoComplete="off"
                  isRequired
                  labelStyle={{ backgroundColor: colors.black_5 }}
                />

                <TextField
                  label={t('Confirm email')}
                  name="confirmEmail"
                  isRequired
                  labelStyle={{ backgroundColor: colors.black_5 }}
                />

                <PasswordField
                  mb="0.8rem"
                  label="Password"
                  name="field_b"
                  isRequired
                  autocomplete="new-password"
                  error={errors.field_b && touched.field_b}
                />
                <PasswordStrength conditionValues={passwordStrength} />

                <ButtonSpacing marginTop="1.25rem">
                  <Button
                    isFetching={status === 'loading'}
                    type="submit"
                    label="Submit"
                    color="blue"
                    width="100%"
                  />
                </ButtonSpacing>

                <Paragraph
                  fontFamily={fontFamily.regular}
                  fontSize="0.75rem"
                  fontWeight="400"
                  letterSpacing="0.013rem"
                  margin="1.25rem 0 0 0"
                  color={colors.black_80}
                  display="block"
                  textAlign="center"
                >
                  {t('This site is protected by reCAPTCHA and the Google')}{' '}
                  <LoginLink
                    onClick={() => openInNewTab('https://policies.google.com/privacy')}
                    to="#"
                  >
                    {t('Privacy policy')}
                  </LoginLink>{' '}
                  {t('and')}{' '}
                  <LoginLink
                    onClick={() => openInNewTab('https://policies.google.com/terms')}
                    to="#"
                  >
                    {t('Terms of service_')}
                  </LoginLink>{' '}
                  {t('apply')}
                </Paragraph>

                <Paragraph
                  fontFamily={fontFamily.regular}
                  fontSize="0.75rem"
                  fontWeight="400"
                  letterSpacing="0.013rem"
                  margin="1.25rem 0 0 0"
                  justifyContent="center"
                  color={colors.black_80}
                >
                  {t('By creating an account, you agree to')}
                </Paragraph>

                <Flex justifyContent="center">
                  <LoginLink onClick={() => openInNewTab(TERMS_OF_SERVICE_URL)} to="#">
                    {t('Terms of service')}
                  </LoginLink>
                  <Paragraph
                    fontFamily={fontFamily.regular}
                    fontSize="0.75rem"
                    fontWeight="400"
                    letterSpacing="0.013rem"
                    margin="0 0.125rem"
                    color={colors.black_80}
                  >
                    {t('and')}
                  </Paragraph>
                  <LoginLink onClick={() => openInNewTab(PRIVACY_POLICY_URL)} to="#">
                    {t('Privacy policy')}
                  </LoginLink>
                </Flex>

                <Divider margin="1.25rem 0" />

                <Flex justifyContent="space-between">
                  <Paragraph
                    fontFamily={fontFamily.regular}
                    fontSize="0.75rem"
                    fontWeight="400"
                    letterSpacing="0.013rem"
                    margin="0"
                    justifyContent="center"
                  >
                    {t('You already have an account?')}
                  </Paragraph>

                  <LoginLink to="/login">{t('Login here')}</LoginLink>
                </Flex>
              </LoginSignupWrapper>
            )}
          </Formik>
        </>
      ) : (
        <PlansAndPricing />
      )}
    </>
  );
};
